import React, { useCallback, useEffect, useRef, useState } from "react";
import { HexColorPicker, HexColorInput } from "react-colorful";

const normalize = hex => {
    if(!hex) return '';
    let value = hex.trim();
    if(value && value[0] !== '#') value = `#${value}`;
    return value.toUpperCase();
};

// Tempo (ms) de inatividade antes de propagar a cor para o componente pai.
// Como debounce: durante um arrasto contínuo o pai NÃO re-renderiza; só recebe
// a cor quando o movimento para (ou ao soltar o mouse). O picker/amostra seguem
// ao vivo pelo estado local, então o drag fica fluido independentemente do peso
// do re-render do pai.
const COMMIT_DELAY = 90;

export const InputColorComponent = ({label, change, value}) => {
    const [open, setOpen] = useState(false);
    const [color, setColor] = useState(normalize(value) || '#000000');
    const containerRef = useRef(null);

    // Mantém a referência do callback atual sem re-criar handlers a cada render.
    const changeRef = useRef(change);
    useEffect(() => { changeRef.current = change; });

    // Throttle da propagação: guarda o último valor pendente e um timer ativo.
    const pendingRef = useRef(null);
    const timerRef = useRef(null);
    // Último valor emitido pro pai, para ignorar o "eco" que ele devolve via prop
    // (senão o sync abaixo resetaria a cor local no meio do drag → engasgo).
    const lastEmittedRef = useRef(null);

    const commit = useCallback(() => {
        if(timerRef.current){
            clearTimeout(timerRef.current);
            timerRef.current = null;
        }
        if(pendingRef.current !== null){
            lastEmittedRef.current = pendingRef.current;
            changeRef.current(pendingRef.current);
            pendingRef.current = null;
        }
    }, []);

    // Sincroniza quando o valor externo muda (ex.: abrir modal de edição),
    // ignorando o eco do próprio valor que acabamos de emitir.
    useEffect(() => {
        const next = normalize(value);
        if(next && next !== color && next !== lastEmittedRef.current){
            setColor(next);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);

    const handleChange = useCallback(hex => {
        const next = normalize(hex);
        setColor(next);            // ao vivo: picker + amostra continuam fluidos
        pendingRef.current = next; // debounce: só propaga quando o movimento para
        if(timerRef.current) clearTimeout(timerRef.current);
        timerRef.current = setTimeout(commit, COMMIT_DELAY);
    }, [commit]);

    // Garante que o último valor chegue ao pai ao fechar o popover / desmontar.
    useEffect(() => {
        if(!open) commit();
    }, [open, commit]);

    useEffect(() => () => commit(), [commit]);

    // Fecha o popover ao clicar fora ou pressionar Esc.
    useEffect(() => {
        if(!open) return;

        const handleClickOutside = event => {
            if(containerRef.current && !containerRef.current.contains(event.target)){
                setOpen(false);
            }
        };
        const handleKey = event => {
            if(event.key === 'Escape') setOpen(false);
        };
        // Ao soltar o mouse/toque, sincroniza o valor final na hora (sem esperar
        // o debounce) — mantém o pai sem re-render durante o arrasto, mas exato ao fim.
        const handleRelease = () => commit();

        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('keydown', handleKey);
        document.addEventListener('pointerup', handleRelease);
        document.addEventListener('touchend', handleRelease);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('keydown', handleKey);
            document.removeEventListener('pointerup', handleRelease);
            document.removeEventListener('touchend', handleRelease);
        };
    }, [open, commit]);

    return (
        <div
            ref={containerRef}
            className={`input-group input-color-group${label ? ' has-label' : ''}${open ? ' open' : ''}`}
        >
            {label &&
                <label>{label}</label>
            }
            <button
                type="button"
                className="input-color-trigger"
                onClick={() => setOpen(prev => !prev)}
            >
                <span className="input-color-swatch" style={{backgroundColor: color}} />
                <span className="input-color-value">{color || '—'}</span>
            </button>

            {open &&
                <div className="input-color-popover">
                    <HexColorPicker color={color} onChange={handleChange} />
                    <div className="input-color-hex">
                        <span className="input-color-hex-prefix">#</span>
                        <HexColorInput color={color} onChange={handleChange} />
                    </div>
                </div>
            }
        </div>
    );
}

export default InputColorComponent;
